// Small card a user can drop into Profile / Security to configure the
// default print target (posto de impressão). Calls /users/print-targets
// to fill the dropdown and /user/update-default-print-target to persist.

import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Utils from "../utils";
import axiosInstance from "../services/AxiosInstance";
import "./print-target-setting.css";

const listPrintTargets = () =>
  axiosInstance.post("/users/print-targets", {});
const updateDefault = (data) =>
  axiosInstance.post("/user/update-default-print-target", data);
const fetchProfile = () => axiosInstance.post("/profile/detail", {});

const PrintTargetSetting = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [targets, setTargets] = useState([]);
  const [selected, setSelected] = useState(null); // null = self
  const [selfId, setSelfId] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [targetsRes, profileRes] = await Promise.all([
        listPrintTargets(),
        fetchProfile(),
      ]);
      const list = targetsRes?.data?.data || [];
      setTargets(list);
      const me = profileRes?.data?.data;
      setSelfId(me?.id ?? null);
      setSelected(me?.default_print_target_user_id ?? null);
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const selectedValue = useMemo(() => selected ?? "self", [selected]);

  const onChange = async (e) => {
    const raw = e.target.value;
    const nextId = raw === "self" ? null : Number(raw);
    setSaving(true);
    try {
      await updateDefault({ target_user_id: nextId });
      setSelected(nextId);
      toast.success(
        nextId == null || nextId === selfId
          ? "Posto de impressão: tu próprio"
          : "Posto de impressão actualizado",
      );
    } catch (err) {
      toast.error(Utils.getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="tp-pts-card tp-pts-loading">
        A carregar posto de impressão…
      </div>
    );
  }

  const selectedUser =
    selected != null ? targets.find((t) => t.id === selected) : null;

  return (
    <div className="tp-pts-card">
      <div className="tp-pts-head">
        <i className="bi bi-printer tp-pts-icon" />
        <div>
          <h3>Posto de impressão por defeito</h3>
          <small>
            Etiquetas criadas por ti são encaminhadas para este posto. Pode
            ser tu próprio (app desktop) ou outro colega com bancada
            partilhada.
          </small>
        </div>
      </div>

      <div className="tp-pts-row">
        <label className="tp-pts-label">Destino:</label>
        <select
          value={selectedValue}
          onChange={onChange}
          disabled={saving}
          className="tp-pts-select"
        >
          <option value="self">🧍 Imprimir no meu posto (padrão)</option>
          {targets
            .filter((t) => !t.is_self)
            .map((t) => (
              <option key={t.id} value={t.id}>
                {t.online ? "🟢" : "⚪"} {t.name}
                {t.online ? "" : " · offline"}
              </option>
            ))}
        </select>
      </div>

      {selectedUser && !selectedUser.online && (
        <div className="tp-pts-warning">
          <i className="bi bi-exclamation-triangle-fill" />
          Este posto está offline. Se a app desktop não estiver aberta
          quando imprimires, a etiqueta fica em fila até abrir a app.
        </div>
      )}
    </div>
  );
};

export default PrintTargetSetting;
